import { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import PageBanner from '@/components/shared/PageBanner';
import CTABanner from '@/components/shared/CTABanner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { publicApi } from '@/lib/api';

const FreeResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.getFreeResources()
      .then(res => setResources(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const assessments = resources.filter(r => r.category === 'assessment');
  const tools = resources.filter(r => r.category === 'tool');
  const otherResources = resources.filter(r => r.category === 'resource');

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        <PageBanner 
          title="Free Resources" 
          breadcrumbs={[{ label: 'Free Resources' }]} 
        />

        {/* Intro Section */}
        <section className="py-16" data-testid="resources-intro">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">FREE RESOURCES</h2>
              <p className="text-lg text-muted-foreground">
                We have included some free career resources. There are free career assessments and job search info 
                to get you started. The assessments will help you to discover interests and strengths.
              </p>
            </div>
          </div>
        </section>

        {/* Free Assessments */}
        {assessments.length > 0 && (
          <section className="py-12 bg-muted/30" data-testid="assessments-section">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h3 className="text-xl font-heading font-bold mb-6">
                Free Assessments
              </h3>
              <p className="text-muted-foreground mb-8">
                Here are some free assessments that will give you more information about yourself, your strengths, and your interests:
              </p>
              <div className="space-y-4">
                {assessments.map((resource, index) => (
                  <Card key={resource.id} className="hover:shadow-md transition-shadow" data-testid={`assessment-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <span className="inline-block w-8 h-8 rounded-full bg-primary text-primary-foreground text-center leading-8 font-bold mr-3">
                            {index + 1}
                          </span>
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-lg font-heading font-bold text-primary hover:underline"
                          >
                            {resource.title}
                          </a>
                          <p className="text-muted-foreground mt-2 ml-11">
                            {resource.description}
                          </p>
                        </div>
                        <a
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 p-2 text-primary hover:bg-primary/10 rounded-full transition-colors"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Banner */}
        <CTABanner 
          title="Find out how a career coach can help change your life."
          buttonText="Get Started"
          openScheduling={true}
        />

        {/* Free Career Tools */}
        {tools.length > 0 && (
          <section className="py-16" data-testid="tools-section">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h3 className="text-xl font-heading font-bold mb-8">Free Career Tools</h3>
              <div className="space-y-6">
                {tools.map((resource, index) => (
                  <div key={resource.id} className="flex items-start gap-4" data-testid={`tool-${index}`}>
                    <span className="inline-block w-8 h-8 rounded-full bg-secondary text-secondary-foreground text-center leading-8 font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <div>
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-heading font-bold text-secondary hover:underline"
                      >
                        {resource.title}
                      </a>
                      <span className="text-muted-foreground ml-1">
                        - {resource.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Investment CTA */}
        <CTABanner />

        {/* Additional Resources */}
        {otherResources.length > 0 && (
          <section className="py-16 bg-muted/30" data-testid="additional-resources-section">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h3 className="text-xl font-heading font-bold mb-8">Additional Resources</h3>
              <div className="space-y-6">
                {otherResources.map((resource, index) => (
                  <div key={resource.id} className="flex items-start gap-4" data-testid={`resource-${index}`}>
                    <span className="inline-block w-8 h-8 rounded-full bg-accent text-accent-foreground text-center leading-8 font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <div>
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-heading font-bold text-accent hover:underline"
                      >
                        {resource.title}
                      </a>
                      <span className="text-muted-foreground ml-1">
                        - {resource.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default FreeResourcesPage;
